/**
 * Sequence Recipients Export — who was actually enrolled, and what they were sent.
 *
 * WHY THIS EXISTS: the existing "Summary Export" emits the dashboard/funnel numbers, which is what
 * its label says. An operator asking "who received this email?" had no way to get that list —
 * on 2026-09-14 the answer had to be pulled from the database by hand after the Lifetime
 * Membership Welcome Journey emailed 940 people it should not have.
 *
 * TRUNCATION IS THE RISK HERE. The Enrollments tab reads a single PostgREST request, which caps at
 * 1000 rows by default — it displayed "1000" for a sequence with 1,465 enrollments. An export built
 * on that query would hand someone a short CSV that looks complete, which is worse than no export
 * at all when it is being used to work out who was contacted. `fetchAllEnrollmentsForExport` pages
 * explicitly and the CSV records the row count so a truncated file is self-evident.
 */

import { guardFormulaInjection, downloadCsv } from "./campaignSummaryCsv";

const EXPORT_TIMEZONE = "America/Chicago";

export interface ExportEnrollmentRow {
  contact_id: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  status: string | null;
  enrolled_at: string | null;
  completed_at?: string | null;
  trigger_source?: string | null;
}

function csvCell(value: string | number | null | undefined): string {
  const s =
    typeof value === "number"
      ? String(value)
      : guardFormulaInjection(value ?? "");
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function row(cells: (string | number | null | undefined)[]): string {
  return cells.map(csvCell).join(",");
}

function fmtTs(iso: string | null | undefined): string {
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  return d.toLocaleString("en-US", {
    timeZone: EXPORT_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function buildSequenceRecipientsCsv(
  sequenceName: string,
  rows: ExportEnrollmentRow[],
): string {
  const lines: string[] = [];
  lines.push(row(["Sequence", sequenceName]));
  lines.push(row(["Exported", fmtTs(new Date().toISOString()) + " CT"]));
  // Stated explicitly so a short file cannot be mistaken for a complete one.
  lines.push(row(["Rows in this file", rows.length]));
  lines.push("");
  lines.push(
    row([
      "Email",
      "First Name",
      "Last Name",
      "Status",
      "Enrolled (CT)",
      "Completed (CT)",
      "Trigger Source",
      "Contact ID",
    ]),
  );
  for (const r of rows) {
    lines.push(
      row([
        (r.email ?? "").toLowerCase(),
        r.first_name,
        r.last_name,
        r.status,
        fmtTs(r.enrolled_at),
        fmtTs(r.completed_at),
        r.trigger_source,
        r.contact_id,
      ]),
    );
  }
  return lines.join("\r\n");
}

export function downloadSequenceRecipientsCsv(
  sequenceName: string,
  rows: ExportEnrollmentRow[],
): void {
  const slug =
    sequenceName
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase() || "sequence";
  downloadCsv(
    `sequence-${slug}-recipients.csv`,
    buildSequenceRecipientsCsv(sequenceName, rows),
  );
}
